import {
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  inject,
  input,
  signal,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import {
  baseCssConfigStyle,
  ButtonLoadingIndicatorType,
  ButtonTypes,
} from '@finxone-platform/shared/sys-config-types';

@Component({
  standalone: false,
  selector: 'finx-button',
  templateUrl: './finx-button.component.html',
  styleUrls: ['./finx-button.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxButtonComponent),
      multi: true,
    },
  ],
})
export class FinxButtonComponent implements ControlValueAccessor, OnInit, OnDestroy {
  el = inject(ElementRef);
  private cd = inject(ChangeDetectorRef);
  private readonly configService = inject(WidgetConfigAccessor);

  isFocused = false;
  @Input() id: string;
  @Input() type = 'button';
  @Input() width = '100%';
  @Input() class = '';
  @Input() labeltext = '';
  @Input() loadingText = '';
  @Input() isLoadingState = false;
  @Input() disabled = false;
  @Input() rounded = true;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() value: any = '';
  @Input() buttonStyle:
    | {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [klass: string]: any;
      }
    | null
    | undefined;
  @Input() leftIcon = '';
  @Input() rightIcon = '';
  @Input() public rightIconSize?:
    | {
        width: string;
        height: string;
      }
    | baseCssConfigStyle;
  @Input() public leftIconSize?:
    | {
        width: string;
        height: string;
      }
    | baseCssConfigStyle;
  @Input() public rightIconClass = '';
  @Input() public isAllowedGlobalStyling = false;
  /** When set > 0, the button starts disabled and becomes enabled after this many seconds, with a left-to-right fill animation. */
  public readonly activationDelaySeconds = input<number>();

  /** Internal gate ORed with the `disabled` input — never replaces its meaning. */
  public readonly delayActive = signal(false);
  private delayEndTimeout?: ReturnType<typeof setTimeout>;

  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelChange: Function = () => {};
  // eslint-disable-next-line @typescript-eslint/ban-types, @typescript-eslint/no-empty-function
  onModelTouched: Function = () => {};

  public borderRadiusClass = '';
  /**
   * Global-styling-driven loading indicator/text, resolved and handlebars-compiled by the caller
   * (cta-button-widget at runtime, finx-button-with-disabled in the Global Styling preview) - this
   * component has no access to the store/handlebars model-building services needed to compile the
   * loading text, so it can only render whatever is passed in, never resolve it itself.
   */
  public readonly globalLoadingIndicatorType = input<ButtonLoadingIndicatorType>(
    ButtonLoadingIndicatorType.ANIMATION,
  );
  public readonly globalLoadingText = input<string>('');
  public readonly loadingIndicatorTypes = ButtonLoadingIndicatorType;

  private cssVars: Record<string, string> = {};
  public buttonTypes = ButtonTypes;
  public activeButtonType: string;
  constructor() {
    effect(() => {
      const globalCssVariables = this.configService.globalCssVariables();
      this.cssVars = globalCssVariables;
      if (this.class && this.isAllowedGlobalStyling) {
        // get the border radius class and icon from theme  css
        this.getStyleValuesForButtonTypes(this.class);
      }
    });
  }

  ngOnInit(): void {
    this.startActivationDelay();
  }

  private startActivationDelay(): void {
    const delaySeconds = this.activationDelaySeconds();
    if (!delaySeconds || delaySeconds <= 0) {
      return;
    }

    this.delayActive.set(true);
    this.delayEndTimeout = setTimeout(() => {
      this.delayActive.set(false);
    }, delaySeconds * 1000);
  }

  private getStyleValuesForButtonTypes(className: string) {
    const buttonClasses = className.split(' ');
    // Create a regex pattern from the button types
    const buttonTypePattern = new RegExp(Object.values(this.buttonTypes).join('|'), 'i');
    // Find the first matching button type
    const matchedButtonType = buttonClasses.find((classItem) => buttonTypePattern.test(classItem));
    this.activeButtonType = matchedButtonType ?? '';
    switch (matchedButtonType) {
      case this.buttonTypes.PRIMARY:
        this.borderRadiusClass = this.getCssVariableValue('--primaryButtonBorderRadiusClass');
        break;
      case this.buttonTypes.TERTIARY:
        this.borderRadiusClass = this.getCssVariableValue('--tertiaryButtonBorderRadiusClass');
        break;
      case this.buttonTypes.SECONDARY:
        this.borderRadiusClass = this.getCssVariableValue('--secondaryButtonBorderRadiusClass');
        break;
      case this.buttonTypes.MENU:
        //get the icon name from root styles
        this.rightIcon = this.getCssVariableValue('--menuButtonIcon');
        this.borderRadiusClass = this.getCssVariableValue('--menuButtonBorderRadiusClass');
        break;
      default:
        this.borderRadiusClass = '';
        break;
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  writeValue(value: any): void {
    this.value = value;
    this.cd.markForCheck();
  }
  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnChange(fn: Function): void {
    this.onModelChange = fn;
  }
  // eslint-disable-next-line @typescript-eslint/ban-types
  registerOnTouched(fn: Function): void {
    this.onModelTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.cd.markForCheck();
  }
  onClick = () => {
    this.isFocused = false;
  };

  private getCssVariableValue(variableName: string): string {
    return this.cssVars[variableName] ?? '';
  }

  ngOnDestroy(): void {
    clearTimeout(this.delayEndTimeout);
  }
}
